import { computed } from 'vue';
import { isNil } from 'lodash';

import { useYardAreaPresenter } from '@satellite/composables/useYardAreaPresenter';
import { useYardAssetPresenter } from '@satellite/composables/useYardAssetPresenter';
import { useYardAssetStatus } from '@satellite/composables/useYardAssetStatus';
import { useYardSpotPresenter } from '@satellite/composables/useYardSpotPresenter';
import { useYardInTransitFeature } from '@satellite/composables/yard/useYardInTransitFeature';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenter';

export interface UseCheckAssetPanelOptions {
  assetId: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;
  initialAssetData?:
    | Ref<Maybe<PresenterPlaceholderData<YardAssetDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<YardAssetDetails>>>;
}

export function useCheckAssetPanel({ assetId, initialAssetData }: UseCheckAssetPanelOptions) {
  const assetPresenter = useYardAssetPresenter({
    assetId,
    placeholderAssetData: initialAssetData,
    headless: true,
    staleTime: 0
  });
  const asset = assetPresenter.data;
  const spotId = assetPresenter.spotId;

  const assetCode = computed(() => asset.value?.code ?? null);
  const assetType = computed(() => asset.value?.type ?? null);

  const spotPresenter = useYardSpotPresenter({ spotId, headless: true });
  const spot = spotPresenter.data;

  const spotCode = computed(() => spot.value?.code ?? null);
  const areaId = computed(() => spot.value?.spotAreaId ?? null);

  const areaPresenter = useYardAreaPresenter({ areaId, headless: true });
  const area = areaPresenter.data;

  const areaName = computed(() => area.value?.name ?? null);

  const isLoading = computed(
    () =>
      assetPresenter.isLoading.value ||
      spotPresenter.isLoading.value ||
      areaPresenter.isLoading.value
  );

  const assetStatus = useYardAssetStatus();
  const { isYardInTransitVisible } = useYardInTransitFeature();

  const isInTransit = computed(
    (): boolean => isYardInTransitVisible.value && assetStatus.isAssetInTransit(asset.value)
  );

  /*
   * Placeholder data carries no events, so isInTransit reads false until they arrive. Location work
   * stays refused through that window rather than letting a tap reassign an asset whose status is
   * not known yet.
   */
  const isLocationBlocked = computed((): boolean => isLoading.value || isInTransit.value);

  const confirmDisabled = computed((): boolean => isNil(spotId.value));

  function onIntersect(entry?: { isIntersecting?: boolean } | boolean): void {
    assetPresenter.onIntersect(entry);
    spotPresenter.onIntersect(entry);
    areaPresenter.onIntersect(entry);
  }

  return {
    asset,
    assetCode,
    assetType,
    spotId,
    spotCode,
    areaId,
    areaName,
    isLoading,
    isInTransit,
    isLocationBlocked,
    confirmDisabled,
    onIntersect
  };
}
